'use client'

import { cn } from '@mdxui/primitives/lib/utils'
import { Copy, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type BundledLanguage, codeToHtml } from 'shiki'

export interface CodeBlockProps {
  /** Code content */
  children: string
  /** Language for syntax highlighting */
  language?: BundledLanguage | string
  /** Optional filename to display */
  filename?: string
  /** Show line numbers */
  lineNumbers?: boolean
  /** Additional className */
  className?: string
}

/**
 * Generates separate HTML for light and dark themes.
 * Returns [lightHtml, darkHtml] tuple for CSS-based theme switching.
 */
async function highlightCode(
  code: string,
  lang: BundledLanguage
): Promise<[string, string]> {
  const [lightHtml, darkHtml] = await Promise.all([
    codeToHtml(code, { lang, theme: 'github-light' }),
    codeToHtml(code, { lang, theme: 'github-dark' }),
  ])

  return [lightHtml, darkHtml]
}

/**
 * CodeBlock component with Shiki syntax highlighting
 *
 * Uses dual themes (github-light/github-dark) with separate HTML outputs
 * and CSS-based visibility toggling for light/dark mode.
 */
export function CodeBlock({
  children,
  language = 'typescript',
  filename,
  className,
}: CodeBlockProps) {
  const [lightHtml, setLightHtml] = useState<string>('')
  const [darkHtml, setDarkHtml] = useState<string>('')
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Clean up the code (remove trailing newline)
  const code = typeof children === 'string' ? children.trim() : String(children).trim()

  useEffect(() => {
    let isMounted = true

    highlightCode(code, language as BundledLanguage)
      .then(([light, dark]) => {
        if (isMounted) {
          setLightHtml(light)
          setDarkHtml(dark)
          setError(null)
        }
      })
      .catch((err) => {
        console.error('[CodeBlock] Highlight error:', err)
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to highlight code')
        }
      })

    return () => {
      isMounted = false
    }
  }, [code, language])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const codeStyles = cn(
    'overflow-x-auto',
    '[&>pre]:m-0 [&>pre]:p-0 [&>pre]:text-sm [&>pre]:!bg-transparent',
    '[&_code]:font-mono [&_code]:text-sm [&_code]:!bg-transparent [&_code]:whitespace-pre'
  )

  return (
    <figure
      className={cn(
        'shiki relative my-4 overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-card text-sm shadow-sm not-prose',
        className
      )}
    >
      {/* Copy button */}
      <div className="absolute top-2 right-2 z-10 backdrop-blur-lg rounded-[var(--radius)] text-muted-foreground empty:hidden">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center justify-center rounded-[var(--radius-md,calc(var(--radius)*0.75))] p-1.5 text-sm font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Copy code"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </button>
      </div>

      {/* Filename header */}
      {filename && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/50">
          <span className="text-xs text-muted-foreground font-mono">{filename}</span>
        </div>
      )}

      {/* Code content */}
      <div
        className="overflow-auto max-h-[600px] py-3.5 text-sm"
        style={{
          paddingLeft: 'calc(var(--spacing, 0.25rem) * 4)',
          paddingRight: 'calc(var(--spacing, 0.25rem) * 8)',
        }}
      >
        {/* Fallback: show raw code while highlighting loads or on error */}
        {/* Note: 'shiki' class tells MarkdownContent to skip this pre element */}
        {!lightHtml && !darkHtml && (
          <pre className="shiki m-0 p-0 text-sm font-mono whitespace-pre text-muted-foreground">
            {error && <div className="text-red-500 text-xs mb-2">Highlight error: {error}</div>}
            <code>{code}</code>
          </pre>
        )}
        {/* Light theme - hidden in dark mode */}
        {lightHtml && (
          <div
            className={cn(codeStyles, 'dark:hidden')}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
            dangerouslySetInnerHTML={{ __html: lightHtml }}
          />
        )}
        {/* Dark theme - visible only in dark mode */}
        {darkHtml && (
          <div
            className={cn(codeStyles, 'hidden dark:block')}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
            dangerouslySetInnerHTML={{ __html: darkHtml }}
          />
        )}
      </div>
    </figure>
  )
}
